/**
 * The change flash — §A5's `cellflash`, and the reason this page is one screen.
 *
 * The point of putting four panels together is that the TD sees what a manual change costs
 * him *at once*: he moves one name and the matrix, the summary and the team coverage all
 * move with it. A recomputed table that simply re-renders makes the reader hunt for the
 * difference, so the cells whose printed value actually moved are marked for a moment.
 *
 * How it works, and why it is this way:
 *
 *  * It compares the **printed values**, not the appointments. A derived cell that did not
 *    move does not flash even when the appointment behind it changed, which is the honest
 *    behaviour — the flash means "this number is different", and nothing else.
 *  * The first render never flashes. Arriving at a populated day is not a change.
 *  * A second change while a flash is running restarts the mark on the cells that moved
 *    again, and leaves the others to finish. Each flash therefore lasts its own 3 seconds
 *    rather than being cut short by unrelated activity elsewhere on the screen.
 */

import { useEffect, useRef, useState } from "react";

/**
 * How long a moved cell stays marked.
 *
 * F-21 — the Technical Delegate, 20 August 2026: *"the animated effect when tables react
 * could be slightly longer, maybe double"*. It was 1.5 seconds, which is long enough to
 * notice a flash and not long enough to read three tables' worth of it while the eye is still
 * on the row that was edited. Doubled to 3 seconds, and the CSS animation with it.
 */
const FLASH_MS = 3000;

export function useCellFlash(values: ReadonlyMap<string, number>): ReadonlySet<string> {
  // Null until the first render has been recorded — see "the first render never flashes".
  const previous = useRef<Map<string, number> | null>(null);
  const [flashing, setFlashing] = useState<ReadonlySet<string>>(new Set());

  // A serialisation of the map, so the effect below re-runs when a VALUE moves rather than
  // whenever the caller happens to build a new Map object.
  const signature = [...values].map(([key, value]) => `${key}=${String(value)}`).join("|");

  useEffect(() => {
    const before = previous.current;
    previous.current = new Map(values);
    if (before === null) return undefined;

    const changed = new Set<string>();
    for (const [key, value] of values) {
      const was = before.get(key);
      if (was !== undefined && was !== value) changed.add(key);
    }
    if (changed.size === 0) return undefined;

    setFlashing((current) => new Set([...current, ...changed]));
    const timer = setTimeout(() => {
      setFlashing((current) => {
        const next = new Set(current);
        for (const key of changed) next.delete(key);
        return next;
      });
    }, FLASH_MS);
    return () => {
      clearTimeout(timer);
    };
    // `values` is rebuilt on every render by design; `signature` is the identity that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return flashing;
}

/** The class list for one derived cell: its heat (or nothing) plus the flash while it runs. */
export function cellClass(base: string, key: string, flashing: ReadonlySet<string>): string {
  return flashing.has(key) ? `${base} cellflash`.trim() : base;
}
